/**
 * `code` values on errors from Firebase Callable Functions (`httpsCallable` / `onCall`).
 * Client SDKs typically prefix with `functions/` (e.g. React Native Firebase).
 *
 * @see https://firebase.google.com/docs/reference/node/firebase-functions.https.HttpsError
 */

/** Prefix for HTTPS error codes returned by the client Functions SDK. */
export const FIREBASE_CALLABLE_ERROR_CODE_PREFIX = "functions/";

/** `HttpsError` code for HTTP 429 / quota-style limits (`resource-exhausted`). */
export const FIREBASE_CALLABLE_RESOURCE_EXHAUSTED_SHORT = "resource-exhausted";

/** Full code string as usually observed on the client (`functions/resource-exhausted`). */
export const FIREBASE_CALLABLE_RESOURCE_EXHAUSTED = `${FIREBASE_CALLABLE_ERROR_CODE_PREFIX}${FIREBASE_CALLABLE_RESOURCE_EXHAUSTED_SHORT}`;

/** `HttpsError` code for HTTP 404 / missing callable or resource (`not-found`). */
export const FIREBASE_CALLABLE_NOT_FOUND_SHORT = "not-found";

/** Full code string as usually observed on the client (`functions/not-found`). */
export const FIREBASE_CALLABLE_NOT_FOUND = `${FIREBASE_CALLABLE_ERROR_CODE_PREFIX}${FIREBASE_CALLABLE_NOT_FOUND_SHORT}`;

/**
 * @param code - Value of `error.code` from a Firebase error object.
 */
export const isFirebaseCallableNotFoundCode = (code: string): boolean =>
  code === FIREBASE_CALLABLE_NOT_FOUND || code === FIREBASE_CALLABLE_NOT_FOUND_SHORT;

/**
 * @param error - Firebase client error object.
 */
export const isFirebaseCallableNotFoundError = (error: unknown): boolean => {
  if (typeof error !== "object" || error === null || !("code" in error)) {
    return false;
  }

  return isFirebaseCallableNotFoundCode(String((error as { code: unknown }).code));
};

/**
 * @param code - Value of `error.code` from a Firebase error object.
 */
export const isFirebaseCallableResourceExhaustedCode = (code: string): boolean =>
  code === FIREBASE_CALLABLE_RESOURCE_EXHAUSTED ||
  code === FIREBASE_CALLABLE_RESOURCE_EXHAUSTED_SHORT ||
  code.endsWith(`/${FIREBASE_CALLABLE_RESOURCE_EXHAUSTED_SHORT}`);

/** Whether `error.code` looks like a Firebase Callable `HttpsError` from the client SDK. */
export const isFirebaseCallableErrorCode = (code: string): boolean =>
  code.startsWith(FIREBASE_CALLABLE_ERROR_CODE_PREFIX);
